/**
 * REQ-0028 批量转船（海运订舱列表）规则纯函数（零依赖，可单测）。
 *
 * 「同船名航次」入口拦截已按需求取消：勾选不同船名/航次的正操作业务也允许批量转船。
 * 本模块承接三类决策，供 bookingTreeGrid.vue 消费：
 *   1) isSameVesselVoyage：所选业务是否同为同一船名+航次（trim 归一后全等）；
 *   2) resolveVesselVoyageChanged：「船名航次有效变更」统一谓词——异构选择时任何
 *      非空输入即视为变更；同构时与原 flagInfo 表达式逐分支等价；
 *   3) resolveBatchTransshipAtd：ATD 下发决策（手工填值 > 手工勾清空 > 改船自动清 > 不下发）。
 * 本模块刻意保持零 import（不引 store/router 链），供 Jest 直接加载（PLAN §0 可测性边界）。
 */

/**
 * 所选业务是否同为同一船名+航次（trim 归一后全等比较；沿用现有
 * `x ? x.trim() : x` 写法，null/'' 边界行为与原拦截代码一致）。
 * 空/单元素数组 → true（防御；handler 只在 jobs.length>0 时运行）。
 */
export function isSameVesselVoyage(jobs) {
  if (!jobs || jobs.length <= 1) {
    return true
  }
  const voyageFlight = jobs[0].voyageFlight ? jobs[0].voyageFlight.trim() : jobs[0].voyageFlight
  const vessel = jobs[0].vessel ? jobs[0].vessel.trim() : jobs[0].vessel
  for (const job of jobs) {
    const curVoyageFlight = job.voyageFlight ? job.voyageFlight.trim() : job.voyageFlight
    const curVessel = job.vessel ? job.vessel.trim() : job.vessel
    if (curVoyageFlight !== voyageFlight || curVessel !== vessel) {
      return false
    }
  }
  return true
}

/**
 * 「船名航次有效变更」统一谓词 —— 同时驱动 船期清空确认弹窗（替代原 flagInfo）
 * 与 ATD 自动清空。异构选择（非同一船名航次）时任何非空输入即视为变更。
 *
 * inputVessel = isDBFlag ? txtVessel_1 : txtVessel；inputVoyage 同理（isVoyages 路由）。
 * 同构时与原 flagInfo 布尔表达式逐分支等价（含 isDBFlag/isVoyages 分支路由）；
 * cleanVesselAndVoyage（勾「清空」船名/航次）并入有效变更（PLAN T1-D2）。
 */
export function resolveVesselVoyageChanged({ jobs, isDBFlag, isVoyages, txtVessel, txtVessel_1, txtVoyage, txtVoyage_1, cleanVesselAndVoyage }) {
  if (!jobs || jobs.length === 0) {
    // 防御：调用方保证 jobs 非空；空数组时退化为仅看清空勾选
    return Boolean(cleanVesselAndVoyage)
  }
  const inputVessel = isDBFlag ? txtVessel_1 : txtVessel
  const inputVoyage = isVoyages ? txtVoyage_1 : txtVoyage
  const heterogeneous = !isSameVesselVoyage(jobs)
  const curVessel = jobs[0].vessel ? jobs[0].vessel.trim() : jobs[0].vessel
  const curVoyageFlight = jobs[0].voyageFlight ? jobs[0].voyageFlight.trim() : jobs[0].voyageFlight
  if (inputVessel && (heterogeneous || inputVessel !== curVessel)) {
    return true
  }
  if (inputVoyage && (heterogeneous || inputVoyage !== curVoyageFlight)) {
    return true
  }
  return Boolean(cleanVesselAndVoyage)
}

/**
 * ATD 下发决策。返回 string | null | undefined：
 *   dfATD 非空      → dfATD        （决议③：手工填值优先，绝不自动清）
 *   手工勾选清空    → null         （AC5 既有手工能力，原样保留）
 *   vesselVoyageChanged → null     （决议②：改船名或仅改航次都自动清）
 *   否则            → undefined    （obj 不含 atd 键 → 后端 containsKey 不命中 → ATD 保持原值）
 */
export function resolveBatchTransshipAtd({ dfATD, manualClearAtd, vesselVoyageChanged }) {
  if (dfATD !== '') {
    return dfATD
  }
  if (manualClearAtd) {
    return null
  }
  if (vesselVoyageChanged) {
    return null
  }
  return undefined
}
